import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { rememberIntent, nextFromSearch } from './postLoginRedirect';
import logoVideo from './video/pexa-logo-2.mp4';
import './AppIDLogin.css';

export default function AppIDLogin() {
  const { login } = useAuth();
  const location = useLocation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isEnded, setIsEnded] = useState(false);
  const params = new URLSearchParams(location.search);
  const verificationRequired = params.get('error') === 'verification_required';
  // A link or QR code may name the destination itself (/login?next=/studies),
  // which is also how ProtectedRoute passes on where the visitor was heading.
  const next = nextFromSearch(location.search);

  useEffect(() => {
    if (next) rememberIntent(next);
  }, [next]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d');
    const scale = 0.5; // render canvas at 50% resolution of the source video

    const setCanvasSize = () => {
      if (video.videoWidth && video.videoHeight) {
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
      }
    };

    const drawFrame = () => {
      try {
        if (canvas.width && canvas.height) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        }
      } catch (_) {
        // no-op, drawing can fail if metadata not ready yet
      }
    };

    const onLoadedMetadata = () => {
      setCanvasSize();
    };

    const onTimeUpdate = () => {
      drawFrame();
    };

    const onEnded = () => {
      // Seek slightly before the end, then draw and freeze last frame to canvas
      const seekHandler = () => {
        drawFrame();
        setIsEnded(true);
        try { video.pause(); } catch (_) {}
        video.removeEventListener('seeked', seekHandler);
      };
      video.addEventListener('seeked', seekHandler);
      try {
        const target = Math.max(0, (video.duration || 0) - 0.033);
        video.currentTime = target;
      } catch (_) {
        drawFrame();
        setIsEnded(true);
        try { video.pause(); } catch (_) {}
        video.removeEventListener('seeked', seekHandler);
      }
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, []);

  useEffect(() => {
    document.body.classList.add('login-body');
    return () => {
      document.body.classList.remove('login-body');
    };
  }, []);

  // Ensure Material Symbols font for the language icon on the sign-in button
  useEffect(() => {
    if (!document.getElementById('material-symbols-outlined-font')) {
      const link = document.createElement('link');
      link.id = 'material-symbols-outlined-font';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined';
      document.head.appendChild(link);
    }
  }, []);

  // Dölj scrollbars och applicera svart bakgrund medan inloggningssidan är aktiv
  useEffect(() => {
    const htmlStyle = document.documentElement.style;
    const bodyStyle = document.body.style;
    const prevHtmlOverflow = htmlStyle.overflow;
    const prevBodyOverflow = bodyStyle.overflow;
    const prevBodyBg = bodyStyle.backgroundColor;
    const prevBodyMargin = bodyStyle.margin;

    // htmlStyle.overflow = 'hidden';
    // bodyStyle.overflow = 'hidden';
    bodyStyle.backgroundColor = '#000';
    bodyStyle.margin = '0';

    return () => {
      htmlStyle.overflow = prevHtmlOverflow;
      bodyStyle.overflow = prevBodyOverflow;
      bodyStyle.backgroundColor = prevBodyBg;
      bodyStyle.margin = prevBodyMargin;
    };
  }, []);

  const handleLogin = async () => {
    try {
      await login();
    } catch (err) {
      console.error("Login failed to initiate:", err);
      alert("Login service is not available, please try again later.");
    }
  };

  return (
    <div
      className="login-page"
    >
      <div
        className="video-frame"
      >
        <video
          ref={videoRef}
          className="login-video"
          src={logoVideo}
          autoPlay
          muted
          playsInline
          preload="auto"
          width="1280"
          height="720"
          aria-label="Pexa intro video"
          style={{ display: isEnded ? 'none' : 'block' }}
        />
        <canvas
          ref={canvasRef}
          className="login-canvas"
          aria-label="Pexa intro video (final frame)"
          style={{ display: isEnded ? 'block' : 'none' }}
        />
      </div>

      <header
        className="login-header"
        aria-label="Application title"
        style={{ width: '100%', textAlign: 'center', color: '#fff'}}
      >
        <h1 className="login-title" style={{ margin: 0, fontWeight: 700 }}>
          Small Airway Atlas<span aria-hidden="true">™</span>
        </h1>
      </header>
      <p className="welcome-text">
        Welcome to PExA’s Small Airway Atlas™ &mdash; A meeting<br/>
        point for molecular data from the small airways.
      </p>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
        <button
          onClick={handleLogin}
          className="login-button"
          aria-label="Sign in"
        >
          <span
            className="material-symbols-outlined"
            aria-hidden="true"
            style={{ fontSize: '20px', marginRight: '8px', verticalAlign: 'middle' }}
          >
            language
          </span>
          <span style={{ verticalAlign: 'middle' }}>Sign In</span>
        </button>
      </div>
      {verificationRequired && (
        <p
          role="alert"
          style={{
            width: '100%',
            textAlign: 'center',
            color: '#ffd9a8',
            marginTop: '12px',
            marginBottom: 0,
            fontWeight: 500,
          }}
        >
          Please confirm your email before signing in.
        </p>
      )}

      <p className="login-alt">
        Don't have an account?{' '}
        <Link className="login-link" to={next ? `/signup?next=${encodeURIComponent(next)}` : '/signup'}>Sign up</Link>
      </p>

      <div className="welcome-text-description">
        <center>
          <p>The <b>PExA Small Airway Atlas (SAA)</b> is a curated database of biomolecules detected in distal airway lining fluid and surfactant collected non-invasively using PExA technology. It defines the measurable molecular landscape of the small airway microenvironment — a lung compartment that has historically been inaccessible in clinical practice.</p>

          <p>By cataloguing biomolecules detectable in PEx samples, the SAA provides a practical foundation for study design and experimental planning. In its current version, the Atlas primarily serves as a structured catalogue illustrating the molecular composition of PEx samples. It functions as a strategic knowledge asset supporting biomarker discovery and translational research in small airway disease.</p>

          <p>The database integrates data from in-house experiments and curated evidence from published studies and continues to expand as new findings emerge. Lipid, metabolite and miRNA datasets will be incorporated in future releases.</p>

          <p>For further information about the content and guidance on how to use the SAA, please refer to the manual page.</p>

          <p>Researchers interested in contributing data are welcome to contact us at <a href="mailto:smallairwayatlas@pexa.se">smallairwayatlas@pexa.se</a>.</p>
        </center>
      </div>
    </div>
  );
}
